/**
 * BodyCompositionHistoryTable — Phase 2.2B Session 2
 *
 * Reads: GET /api/nutrition/body-composition/{athlete_id}
 * Deletes: DELETE /api/nutrition/body-composition/{record_id}
 *   - Allowed for ADMIN + NUTRITIONIST + SUPERADMIN (Phase 2.2B inline override).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, Loader2, History } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';

const fmt = (v, dp = 1, s = '') => v === null || v === undefined ? '—' : `${Number(v).toFixed(dp)}${s}`;

const METHOD_BADGE = {
  SKINFOLDS: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  BIA: 'bg-violet-500/20 text-violet-300 border-violet-500/40',
  DEXA: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  MANUAL: 'bg-zinc-700/40 text-zinc-300 border-zinc-600',
};

const BodyCompositionHistoryTable = ({ athleteId, refreshKey }) => {
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  // Phase 2.2B: body comp delete is ADMIN + NUTRITIONIST + SUPERADMIN.
  const canDelete = ['ADMIN', 'NUTRITIONIST', 'SUPERADMIN'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/nutrition/body-composition/${athleteId}`);
      setRows(res.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api, athleteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'IT' ? 'Eliminare definitivamente?' : 'Delete permanently?')) return;
    setDeleting(id);
    try {
      await api.delete(`/nutrition/body-composition/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="bodycomp-history-table">
      <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
        <History className="w-4 h-4 text-zinc-400" />
        <h3 className="text-sm font-semibold text-zinc-200">
          {language === 'IT' ? 'Storico composizione corporea' : 'Body composition history'}
        </h3>
        <span className="text-xs text-zinc-500 ml-1">({rows.length})</span>
      </div>
      {loading ? (
        <div className="p-8 flex items-center justify-center text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> {language === 'IT' ? 'Caricamento…' : 'Loading…'}
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-zinc-500 text-sm" data-testid="bodycomp-history-empty">
          {language === 'IT' ? 'Nessuna misurazione registrata.' : 'No measurements recorded yet.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-zinc-950 text-zinc-400 uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Data' : 'Date'}</th>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Metodo' : 'Method'}</th>
                <th className="px-3 py-2 text-right">BF %</th>
                <th className="px-3 py-2 text-right">{language === 'IT' ? 'Grassa' : 'Fat'}</th>
                <th className="px-3 py-2 text-right">{language === 'IT' ? 'Magra' : 'Lean'}</th>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Protocollo' : 'Protocol'}</th>
                {canDelete && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-800/40" data-testid={`bodycomp-row-${r.id}`}>
                  <td className="px-3 py-2 text-zinc-200">{r.date}</td>
                  <td className="px-3 py-2">
                    <Badge className={`${METHOD_BADGE[r.method] || ''} text-[10px]`}>{r.method}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-100">{fmt(r.body_fat_pct_final, 1, ' %')}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{fmt(r.fat_mass_kg_final, 1, ' kg')}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{fmt(r.lean_mass_kg_final, 1, ' kg')}</td>
                  <td className="px-3 py-2 text-zinc-500">{r.protocol !== 'NA' ? r.protocol : '—'}</td>
                  {canDelete && (
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleDelete(r.id)}
                        disabled={deleting === r.id}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2"
                        data-testid={`bodycomp-delete-${r.id}`}
                      >
                        {deleting === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default BodyCompositionHistoryTable;
